import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { X, Check, Copy, Smartphone, ArrowRight, CheckCircle2, MessageCircle, Sparkles } from "lucide-react";
import { Reveal } from "./anim";
import { CONSULT_BENEFITS, CONSULT_PRICE, UPI_ID, UPI_LINK, UPI_NAME } from "./data";

const API = process.env.REACT_APP_BACKEND_URL;
const WA_NUMBER = "918696938848";

const inputCls =
  "w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg))] px-4 py-3.5 text-base text-[hsl(var(--fg))] placeholder:text-[hsl(var(--muted))] outline-none transition-colors duration-300 focus:border-[hsl(var(--primary))]";
const primaryBtn =
  "flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] px-8 py-4 text-base font-semibold text-[hsl(var(--primary-fg))] transition-transform duration-300 hover:scale-[1.02]";

const OVERLAY = { opacity: 0 };
const MODAL_IN = { opacity: 1, y: 0, scale: 1 };
const MODAL_OUT = { opacity: 0, y: 30, scale: 0.97 };
const SPRING = { type: "spring", stiffness: 300, damping: 26 };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const DetailsStep = ({ form, set, error, next }) => (
  <form onSubmit={next}>
    <p className="font-head text-2xl font-semibold">Premium Consultation — ₹{CONSULT_PRICE}</p>
    <p className="mt-1 text-sm text-[hsl(var(--muted))]">Step 1 of 3 — your details</p>
    <div className="mt-6 grid gap-4">
      <input data-testid="consult-name" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
      <input data-testid="consult-phone" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder="WhatsApp number *" className={inputCls} />
      <input data-testid="consult-email" type="email" value={form.email} onChange={set("email")} placeholder="Email (for confirmation) *" className={inputCls} />
      <input data-testid="consult-note" value={form.note} onChange={set("note")} placeholder="What's the project about? (optional)" className={inputCls} />
    </div>
    {error && <p data-testid="consult-error" className="mt-3 text-sm text-red-400">{error}</p>}
    <button type="submit" data-testid="consult-next-btn" className={`mt-5 ${primaryBtn}`}>
      Continue to Payment <ArrowRight size={17} />
    </button>
  </form>
);

const PayStep = ({ next }) => {
  const [copied, setCopied] = useState(false);
  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div>
      <p className="font-head text-2xl font-semibold">Pay ₹{CONSULT_PRICE} via UPI</p>
      <p className="mt-1 text-sm text-[hsl(var(--muted))]">
        Step 2 of 3 — paying to <span className="text-[hsl(var(--fg))]">{UPI_NAME}</span>, Founder · Design4you Agency
      </p>
      <div className="mt-5 flex justify-center rounded-2xl bg-white p-5">
        <QRCodeSVG value={UPI_LINK} size={180} data-testid="consult-qr" />
      </div>
      <div className="mt-4 flex items-center justify-between rounded-xl border border-[hsl(var(--border))] px-4 py-3">
        <span data-testid="consult-upi-id" className="text-sm font-medium">{UPI_ID}</span>
        <button onClick={copyUpi} data-testid="consult-copy-upi-btn" className="flex items-center gap-1.5 text-sm text-[hsl(var(--primary))]">
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <a href={UPI_LINK} data-testid="consult-upi-app-btn" className={`mt-3 ${primaryBtn}`}>
        <Smartphone size={17} /> Open GPay / UPI App
      </a>
      <p className="mt-2 text-center text-xs text-[hsl(var(--muted))]">On desktop? Scan the QR with any UPI app.</p>
      <button onClick={next} data-testid="consult-paid-btn" className="mt-4 w-full rounded-full border border-[hsl(var(--primary))] px-8 py-4 text-base font-semibold text-[hsl(var(--primary))] transition-colors duration-300 hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-fg))]">
        I've made the payment →
      </button>
    </div>
  );
};

const ConfirmStep = ({ form, set, submitting, error, confirm }) => (
  <div>
    <p className="font-head text-2xl font-semibold">Confirm your booking</p>
    <p className="mt-1 text-sm text-[hsl(var(--muted))]">
      Step 3 of 3 — add your UPI transaction ID so we can verify faster (find it in GPay → payment details)
    </p>
    <input data-testid="consult-txn" value={form.txn_id} onChange={set("txn_id")} placeholder="UPI Transaction ID (recommended)" className={`mt-5 ${inputCls}`} />
    {error && <p data-testid="consult-confirm-error" className="mt-3 text-sm text-red-400">{error}</p>}
    <button onClick={confirm} disabled={submitting} data-testid="consult-confirm-btn" className={`mt-5 ${primaryBtn} disabled:opacity-60`}>
      {submitting ? "Confirming..." : "Confirm Booking"}
    </button>
  </div>
);

const SuccessStep = ({ form, close }) => {
  const waText = encodeURIComponent(
    `Hi Design4you! I just booked the Rs.999 Premium Consultation.\nName: ${form.name}\nWhatsApp: ${form.phone}${form.txn_id ? `\nTxn ID: ${form.txn_id}` : ""}`
  );
  return (
    <div data-testid="consult-success" className="text-center">
      <CheckCircle2 size={44} className="mx-auto text-[hsl(var(--primary))]" />
      <p className="mt-4 font-head text-2xl font-semibold">Booking received, {form.name.split(" ")[0]}! 🎉</p>
      <p className="mt-2 text-sm text-[hsl(var(--muted))]">
        Confirmation email sent to <span className="text-[hsl(var(--fg))]">{form.email}</span>. We'll verify your payment and call you on WhatsApp for a quick 10-minute intro call, then schedule your detailed session.
      </p>
      <a
        href={`https://wa.me/${WA_NUMBER}?text=${waText}`}
        target="_blank"
        rel="noreferrer"
        data-testid="consult-whatsapp-btn"
        className={`mt-6 ${primaryBtn}`}
      >
        <MessageCircle size={17} /> Ping us on WhatsApp too
      </a>
      <button onClick={close} data-testid="consult-done-btn" className="mt-3 text-sm text-[hsl(var(--muted))] underline-offset-4 hover:underline">
        Done, close this
      </button>
    </div>
  );
};

export const PaidConsult = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", phone: "", email: "", note: "", txn_id: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const openModal = () => {
    setForm({ name: "", phone: "", email: "", note: "", txn_id: "" });
    setError("");
    setStep(1);
    setOpen(true);
  };
  const close = () => setOpen(false);
  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
    setError("");
  };

  const next = (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, "");
    if (!form.name.trim()) return setError("Please fill your name.");
    if (digits.length < 8 || digits.length > 15) return setError("Please enter a valid WhatsApp number.");
    if (!EMAIL_RE.test(form.email.trim())) return setError("Please enter a valid email.");
    setError("");
    setStep(2);
  };

  const confirm = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/consult-bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setStep(4);
    } catch (err) {
      console.error("Consult booking failed:", err);
      setError("Something went wrong. Please try again or WhatsApp us at +91 86969 38848.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consult" data-testid="consult-section" className="relative mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-36">
      <div className="overflow-hidden rounded-3xl border border-[hsl(var(--primary)/0.35)] bg-[hsl(var(--bg-soft))]">
        <div className="grid gap-10 p-8 md:grid-cols-2 md:p-14">
          <Reveal>
            <p className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[hsl(var(--muted))]">
              <span className="h-px w-8 bg-[hsl(var(--primary))]" /> Premium Consultation
            </p>
            <h2 className="font-head text-4xl font-semibold leading-[1.05] sm:text-5xl">
              Go deeper for just <span className="italic text-[hsl(var(--primary))]">₹{CONSULT_PRICE}.</span>
            </h2>
            <p className="mt-4 max-w-md text-base text-[hsl(var(--muted))]">
              A detailed working session with the founder — walk away with a real plan, not just a chat.
            </p>
            <div className="mt-6 rounded-xl border border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.08)] px-5 py-4">
              <p className="flex items-start gap-2 text-sm">
                <Sparkles size={17} className="mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
                <span>
                  Decide to work with us afterwards? <strong>Your ₹{CONSULT_PRICE} is fully adjusted in the project cost</strong> — the session effectively becomes free.
                </span>
              </p>
            </div>
            <button onClick={openModal} data-testid="consult-book-btn" className="group mt-8 flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-9 py-4 text-base font-semibold text-[hsl(var(--primary-fg))] transition-transform duration-300 hover:scale-105">
              Book for ₹{CONSULT_PRICE}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mb-5 text-sm uppercase tracking-[0.25em] text-[hsl(var(--muted))]">What you get</p>
            <ul className="space-y-4">
              {CONSULT_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]">
                    <Check size={14} />
                  </span>
                  <span className="text-base">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={OVERLAY}
            animate={{ opacity: 1 }}
            exit={OVERLAY}
            data-testid="consult-modal-overlay"
            className="fixed inset-0 z-[150] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={close}
          >
            <motion.div
              initial={MODAL_OUT}
              animate={MODAL_IN}
              exit={MODAL_OUT}
              transition={SPRING}
              data-testid="consult-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Book premium consultation"
              className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-soft))] p-7 md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={close} data-testid="consult-close-btn" aria-label="Close" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted))] transition-colors hover:text-[hsl(var(--fg))]">
                <X size={16} />
              </button>
              {step === 1 && <DetailsStep form={form} set={set} error={error} next={next} />}
              {step === 2 && <PayStep next={() => setStep(3)} />}
              {step === 3 && <ConfirmStep form={form} set={set} submitting={submitting} error={error} confirm={confirm} />}
              {step === 4 && <SuccessStep form={form} close={close} />}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
